import { useMenu } from 'react-instantsearch';
import {
    StyledResultsItem,
    StyledCategoriesFiltersContainer,
    StyledButtonRemove,
    StyledResultsList,
    StyledResultsListShadow,
    StyledResultsListWrapper,
    StyledCategoriesFiltersContent,
    StyledResultsItemDesktop,
} from './styled';
import React, { Fragment, memo, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { matchSearchQuery } from '~/features/navigation/utils';
import { getUrlQuery } from '~/shared/utils/request';
import { useMedia } from 'react-use';
import { queries, Spacer } from '~/theme';
import ClearIcon from '$icons/close.svg';
import { useAlgoliaAnalytics } from '~/shared/hooks';
import { useFilterUIState } from '$features/productsList/hooks';
import { useInView } from 'framer-motion';

type CategoriesFiltersProps = {
    attribute: string;
    limit?: number;
};

export const CategoriesFilters = memo(({ attribute, limit }: CategoriesFiltersProps) => {
    const { items, refine } = useMenu({ attribute, limit });
    const router = useRouter();
    const [query, setQuery] = useState('');
    const isDesktop = useMedia(queries.md, false);
    const { trackSelectedCategory } = useAlgoliaAnalytics();
    const { setIconicFilterButtonsIsOutOfView } = useFilterUIState();

    useEffect(() => {
        getUrlQuery(
            router.asPath,
            (callbackQuery: string) => {
                setQuery(callbackQuery);
                return '';
            },
            query
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.asPath]);

    const refineCategories = (value: string) => {
        if (value) {
            trackSelectedCategory(`categoryKeys:${value}`);
        }
        refine(value);
    };

    const boldMatchingString = useMemo(() => matchSearchQuery, []);

    const filterJustIconsButtons = useRef<HTMLDivElement | null>(null);
    const filterJustIconsButtonsInView = useInView(filterJustIconsButtons);

    useEffect(() => {
        setIconicFilterButtonsIsOutOfView(!filterJustIconsButtonsInView);
    }, [filterJustIconsButtonsInView, setIconicFilterButtonsIsOutOfView]);

    return (
        <StyledCategoriesFiltersContainer>
            <Spacer xs="4" sm="5" />
            <StyledCategoriesFiltersContent>
                {items.length ? (
                    <StyledResultsListWrapper>
                        <StyledResultsList>
                            {items.map(({ label, count, value, isRefined }) => {
                                return (
                                    <Fragment key={`${count}${value}`}>
                                        {isDesktop && (
                                            <StyledResultsItemDesktop
                                                key={label}
                                                size="sm"
                                                variant={'tertiary'}
                                                as="button"
                                                showHoverIndicator={false}
                                                onClick={() => refineCategories(value)}
                                            >
                                                <span>
                                                    {boldMatchingString(label, query)}&nbsp;
                                                    <span>({count})</span>{' '}
                                                </span>
                                            </StyledResultsItemDesktop>
                                        )}
                                        {!isDesktop && (
                                            <StyledResultsItem
                                                key={label}
                                                size="sm"
                                                variant={'secondary'}
                                                as="button"
                                                shape="icon"
                                                rounded
                                                onClick={() => refineCategories(value)}
                                            >
                                                <span>
                                                    {boldMatchingString(label, query)}&nbsp;
                                                    <span>({count})</span>{' '}
                                                </span>
                                                {isRefined && (
                                                    <StyledButtonRemove
                                                        children={<ClearIcon />}
                                                    />
                                                )}
                                            </StyledResultsItem>
                                        )}
                                    </Fragment>
                                );
                            })}
                            <StyledResultsListShadow />
                        </StyledResultsList>
                    </StyledResultsListWrapper>
                ) : null}
                {/* <MobileFilterButtons ref={filterJustIconsButtons} /> */}
            </StyledCategoriesFiltersContent>
        </StyledCategoriesFiltersContainer>
    );
});
